import type { AgentGradualUpdateData } from "@/_store/back-office/back-office.module";
import type {
  GetDeviceAllowBlockListParameters,
  GetEdrProcessesListParams,
  GetEdrTelemetryListParams,
  GetProcessGraphParams,
  GetUsersRequestParameters,
  UnixTimeFrame,
} from "@/_helpers/api.types";
import type { Pagination } from "@/types";
import type { DeviceAllowBlockListItem } from "@/_store/endpoint-security/device-allow-block-list.module";
import type { RolePermissions } from "@/_store/roles.module";
import type { Service } from "@/constants/cloud-apps";
import type { GroupProtectionStatus } from "@/constants/users";
import type { SecureWebGatewaySettings } from "@/_store/network/swg/secure-web-gateway.module.ts";
import type { VirtualOfficeSettings } from "@/_store/network/virtual-office.module";
import type { ProcessGraphAction } from "@/constants/edr";
import type { AxiosRequestConfig } from "axios";
import type { ApplyPolicyFilters } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import type { UsersAndDevicesTrendExportData } from "@/_store/msp/msp-dashboard.module.ts";
import type { TicketTrigger } from "@/constants/tickets.ts";
import type { SubscriptionType } from "@/constants/workplaces.ts";
import type { SetupHubStep } from "@/constants/setup-hub.ts";
import type { EndpointDgExclusionType } from "@/constants/endpoint-data-governance";
import type { DevicePolicyType } from "@/constants/devices";

export default {
  login: {
    url: "/login",
    method: "post",
  },
  socialSignUp: () => {
    return {
      url: "/sign-up/social",
      method: "POST",
    };
  },
  acceptInvite: {
    url: "/admin-users/joinWorkspace",
    method: "post",
  },
  socialLogin: (service: Service, brandingAlias: string | undefined = undefined) => {
    return {
      url: "/login",
      method: "post",
      params: {
        service,
        brandingAlias,
      },
    };
  },
  promoCodes: () => {
    return {
      url: "/backoffice/promo-codes",
    };
  },
  discoveryServiceStats: () => {
    return {
      url: "/backoffice/discovery-service-stats",
    };
  },
  deactivatePromoCode: (id: string) => {
    return {
      url: `/backoffice/promo-codes/deactivate/${id}`,
      method: "put",
    };
  },
  deletePromoCode: (id: string) => {
    return {
      url: `/backoffice/promo-codes/${id}`,
      method: "delete",
    };
  },
  activatePromoCode: (id: string) => {
    return {
      url: `/backoffice/promo-codes/activate/${id}`,
      method: "put",
    };
  },
  searchPromoCodeSalesAdministrators: (search: string = "") => {
    return {
      url: `/backoffice/promo-codes/sales`,
      params: {
        search,
      },
    };
  },
  searchPromoCodeChannelWorkspaces: (search: string, { page, pageSize }: Pagination) => {
    return {
      url: `/backoffice/promo-codes/channel-workspaces`,
      params: {
        search,
        page,
        pageSize,
      },
    };
  },
  exportPromoCodesCsv: {
    url: "/backoffice/promo-codes/csv/export",
    headers: {
      "Content-Type": "multipart/form-data",
    },
  },
  logout: () => {
    return {
      url: `/logout`,
    };
  },
  refreshToken: () => {
    return {
      url: "/refreshToken",
    };
  },
  adminUsers: {
    url: "/admin-users",
  },
  adminUser: (id: string) => {
    return {
      url: `/admin-users/${id}`,
    };
  },
  adminUserContentInspectionPermissions: (id: string) => {
    return {
      url: `/admin-users/${id}/content-inspection-permissions`,
    };
  },
  getPsaGradientConnector: () => ({
    url: "/settings/connectors/gradient",
  }),
  getPsaConnectwiseConnector: () => ({
    url: "/settings/connectors/connectwise",
  }),
  getPsaSupportedModules: () => ({
    url: "/psa/supported-modules",
  }),
  getConnectwiseBoards: () => ({
    url: "/settings/connectors/connectwise/mapping/service-boards",
  }),
  getConnectwiseTicketStatuses: (serviceBoardId: string) => ({
    url: `/settings/connectors/connectwise/mapping/service-boards/${serviceBoardId}/statuses`,
  }),
  deleteConnectwiseMapping: () => ({
    url: "/settings/connectors/connectwise/mapping/ticket",
  }),
  getPsaAutotaskConnector: () => ({
    url: "/settings/connectors/autotask",
  }),
  connectwiseWorkspacesMapping: () => ({
    url: "/settings/connectors/connectwise/mapping/workspaces",
  }),
  connectwiseCompanies: () => ({
    url: "/settings/connectors/connectwise/mapping/companies",
  }),
  connectwiseServicesMapping: () => ({
    url: "/settings/connectors/connectwise/mapping/services",
  }),
  connectwiseTicketsMapping: () => ({
    url: "/settings/connectors/connectwise/mapping/tickets",
  }),
  connectwiseAutotaskMapping: () => ({
    url: "/settings/connectors/autotask/mapping/tickets",
  }),
  connectwiseProducts: () => ({
    url: "/settings/connectors/connectwise/mapping/products",
  }),
  siemConnectors: () => ({
    url: "/settings/connectors/siem",
  }),
  siemConnector: (id: string) => ({
    url: `/settings/connectors/siem/${id}`,
  }),
  syncSiemConnector: () => ({
    url: "/settings/connectors/siem/sync",
  }),
  webhooks: () => ({
    url: "/settings/connectors/webhooks",
  }),
  webhook: (id: string) => ({
    url: `/settings/connectors/webhooks/${id}`,
  }),
  updateWebhook: () => ({
    url: `/settings/connectors/webhook`,
  }),
  testWebhook: (id: string) => ({
    url: `/settings/connectors/webhooks/${id}/test`,
  }),
  webhookTriggers: () => ({
    url: "/settings/connectors/webhooks/availableTriggers",
  }),
  syslogs: () => ({
    url: "/settings/connectors/syslogs",
  }),
  connector: (id: string) => ({
    url: `/settings/connectors/${id}`,
  }),
  autotaskWorkspacesMapping: () => ({
    url: "/settings/connectors/autotask/mapping/workspaces",
  }),
  autotaskCompanies: () => ({
    url: "/settings/connectors/autotask/mapping/companies",
  }),
  autotaskServicesMapping: () => ({
    url: "/settings/connectors/autotask/mapping/services",
  }),
  autotaskProducts: () => ({
    url: "/settings/connectors/autotask/mapping/products",
  }),
  resendInvite: (id: string) => {
    return {
      url: `/admin-users/${id}/resendInvite`,
      method: "post",
    };
  },
  generateInviteLink: (id: string) => {
    return {
      url: `/admin-users/${id}/generateInviteLink`,
      method: "post",
    };
  },
  myAccount: {
    url: "/portal/account",
  },
  changePassword: {
    url: "/portal/account/changePassword",
  },
  getNotificationsSettings: {
    url: "/portal/account/notifications",
    method: "GET",
  },
  getActivityLogs: () => {
    return {
      url: "/activityLogs",
    };
  },
  activityLogsUndo: (id: string) => ({
    method: "POST",
    url: "/activityLogs/undo",
    data: {
      id,
    },
  }),
  activityLogsExportCSV: {
    method: "POST",
    url: "/activityLogs/export/csv",
  },
  dataLossPermissions: () => ({
    url: "/settings/dataLoss/dataPolicies",
  }),
  monitoredDataPolicies: () => ({
    url: "/settings/dataLoss/monitoredDataPolicies",
  }),
  userDataGovernanceSettings: () => ({
    url: "/settings/dataLoss/monitoring",
  }),
  privacyParametersConfig: () => ({
    url: "/settings/dataGovernance/monitoring/config",
  }),
  userDataGovernanceExclusions: () => ({
    url: "/settings/dataLoss/exclusions",
  }),
  securityTrainingSettings: () => ({
    url: "/security-awareness/settings",
  }),
  deleteSatConnection: (provider: string) => ({
    url: `/security-awareness/settings/providers/${provider}`,
    method: "DELETE",
  }),
  phishingSimulations: () => ({
    url: "/settings/security-awareness/phishing/simulations",
  }),
  exportPhishingSimulationToCsv: () => {
    return {
      url: "/summary-reports/simulations/export/csv",
    };
  },
  securityAwarenessEmailTemplate: () => ({
    url: "/settings/security-awareness/email-template",
  }),
  sendSecurityAwarenessTestEmails: () => {
    return {
      url: "/security-awareness/email/send",
      method: "POST",
    };
  },
  confirmSecurityAwarenessEmails: () => {
    return {
      url: "/security-awareness/settings/email/confirm",
      method: "POST",
    };
  },
  securityAwarenessMetadata: () => {
    return {
      url: "/security-awareness/settings/metadata",
    };
  },
  dismissSaBanner: () => {
    return {
      url: "/security-awareness/settings/dismiss-banner",
      method: "PUT",
    };
  },
  coursePreviewRecipients: () => {
    return {
      url: "/security-awareness/course-preview/recipients",
    };
  },
  coursePreview: () => {
    return {
      url: "/security-awareness/course-preview",
      method: "POST",
    };
  },
  sendSecurityAwarenessTraining: () => {
    return {
      url: "/security-awareness/resend-training",
      method: "POST",
    };
  },
  resetJourney: () => {
    return {
      url: "/security-awareness/reset-journey",
      method: "POST",
    };
  },
  allowlistAppLink: () => {
    return {
      url: "/assets/sat/setup-app",
    };
  },
  getBackOfficeData: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/backoffice",
      params: {
        fromTime,
        toTime,
      },
    };
  },
  globalAdminUsers: {
    url: "/admin-users/global",
  },
  globalAdminUser: (id: string) => {
    return {
      url: `/admin-users/global/${id}`,
    };
  },
  globalAdminUsersCandidates: () => {
    return {
      url: "/admin-users/global/candidates",
    };
  },
  resetGlobalAdminMfa: (email: string) => {
    return {
      url: `/admin-users/global/${email}/mfa`,
      method: "DELETE",
    };
  },
  getUserPermissions: (workspaceId: string) => {
    return {
      url: `/portal/account/${workspaceId}/permissions`,
    };
  },
  getComments: ({ eventId, page = 0, pageSize = 5 }: { eventId: string } & Pagination) => {
    return {
      url: `events/${eventId}/comments`,
      method: "GET",
      params: {
        page,
        pageSize,
      },
    };
  },
  addComment: (eventId: string) => {
    return {
      url: `/events/${eventId}/comments`,
      method: "POST",
    };
  },
  apiCredentials: () => ({
    url: "/oauth/credentials",
  }),
  getUserInfo: (userId: string) => {
    return {
      url: `/dashboard/users/${userId}`,
      method: "GET",
    };
  },
  getUserTickets: (userId: string) => {
    return {
      url: `/dashboard/users/${userId}/tickets`,
      method: "GET",
    };
  },
  getUserActivityLogs: (userId: string) => {
    return {
      url: `/dashboard/users/${userId}/activityLogs`,
      method: "GET",
    };
  },
  getUserAssociatedDevices: (userId: string) => {
    return {
      url: `/users/${userId}/devices`,
      method: "GET",
    };
  },
  protectedUsersSingleAction: (userId: string) => ({
    url: `/dashboard/users/${userId}/actions/perform`,
    method: "POST",
  }),
  protectedUsersBulkAction: () => ({
    url: `/dashboard/users/actions/perform`,
    method: "POST",
  }),
  singleUserActionsList: (userId: string) => ({
    url: `/dashboard/users/${userId}/actions/available`,
    method: "GET",
  }),
  usersListActions: () => ({
    url: "/dashboard/users/actions/available",
    method: "POST",
  }),
  availableDeviceActions: {
    url: "/devices/available/actions",
    method: "POST",
  },
  availableMspDeviceActions: {
    url: "/msp/devices/available-actions",
    method: "POST",
  },
  getMonitoringSettingsByWorkspace: {
    url: "/msp/devices/settings/endpointDataGovernance/monitoring",
  },
  getDeviceSettingsByWorkspace: {
    url: "/msp/devices/settings/workspace-device-settings",
  },
  getUsers: ({
    page,
    pageSize,
    hasVulnerabilities,
    vulnerability,
    search,
    widget,
    labels,
    cloudServices,
    suspended,
    moreFilters,
    mfaEnabled,
    mfaDisabled,
  }: GetUsersRequestParameters) => {
    const params = {
      page,
      pageSize,
      hasVulnerabilities,
      vulnerability,
      search,
      widget,
      labels,
      cloudServices,
      suspended,
      moreFilters,
      mfaEnabled,
      mfaDisabled,
    };

    return {
      url: "/dashboard/users",
      params,
    };
  },
  getWorkspaceMFAConfig: {
    url: "/workspaces/mfaConfig",
    method: "GET",
  },
  toggleWorkspaceMFA: {
    url: "/workspaces/updateMfaConfig",
    method: "PUT",
  },
  deleteMFA: (email: string) => {
    return {
      url: `/portal/account/${email}/mfa`,
      method: "DELETE",
    };
  },
  getCurrentWorkspace: {
    url: "/workspaces/current",
  },
  endpointDataGovernanceSettings: {
    url: "/settings/endpointDataGovernance/monitoring",
  },
  sensitiveDataScans: {
    url: "/settings/endpointDataGovernance/sensitiveDataScans",
  },
  getEndpointDgExclusions: ({
    search,
    type,
    page,
    pageSize,
  }: {
    search?: string | null;
    type?: string;
    page: number;
    pageSize: number;
  }) => {
    return {
      url: "/settings/endpointDataGovernance/exclusions",
      params: {
        search,
        type,
        page,
        pageSize,
      },
    };
  },
  addEndpointDgExclusion: (data: {
    path: string;
    type: EndpointDgExclusionType;
    description?: string;
    environmentVariableKey?: string;
  }) => {
    return {
      url: "/settings/endpointDataGovernance/exclusions",
      method: "POST",
      data,
    };
  },
  deleteEndpointDgExclusion: (id: string) => {
    return {
      url: `/settings/endpointDataGovernance/exclusions/${id}`,
      method: "DELETE",
    };
  },
  uploadEndpointDgExclusionsCsv: (data: FormData) => {
    return {
      url: "/settings/endpointDataGovernance/exclusions/upload",
      method: "POST",
      data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  endpointDgExclusionsTemplate: {
    url: "/settings/endpointDataGovernance/exclusions/upload/template",
  },
  malwareScans: {
    url: "/settings/endpointSecurity/malwareScans",
  },
  searchLabels: (search: string) => {
    return {
      url: "/devices/labels",
      params: {
        search,
      },
    };
  },
  getSocContacts: {
    url: "/settings/soc",
  },
  editSocContacts: (id: string) => {
    return {
      url: `/settings/soc`,
      method: "PUT",
      params: {
        id,
      },
    };
  },
  socRemediation: {
    url: "/settings/soc/remediation",
  },
  workplaces: ({
    name,
    page,
    pageSize,
    type,
  }: {
    name: string;
    page: number;
    pageSize: number;
    type?: string;
  }) => {
    return {
      url: "/workspaces",
      params: {
        name,
        page,
        pageSize,
        type,
      },
    };
  },
  pendingInvitesToWorkplace: (name: string, pagination: Pagination) => {
    return {
      url: "/workspaces/pendingInvites",
      params: {
        name,
        page: pagination.page,
        pageSize: pagination.pageSize,
      },
    };
  },
  joinWorkplace: (id: string) => {
    return {
      url: `/workspaces/join/${id}`,
      method: "post",
    };
  },
  getDeviceAllowBlocklist: ({
    type,
    search,
    page,
    pageSize,
  }: GetDeviceAllowBlockListParameters) => {
    return {
      url: "/settings/devices/allowBlockList",
      params: {
        types: type,
        search,
        page,
        pageSize,
      },
    };
  },
  deleteDeviceAllowBlocklist: (id: string) => {
    return {
      url: `/settings/devices/allowBlockList/${id}`,
      method: "DELETE",
    };
  },
  deviceAllowBlockListInheritanceSettings: {
    url: "/settings/devices/allowBlockList/inheritanceEnabled",
  },
  addDeviceAllowBlockList: (data: DeviceAllowBlockListItem) => {
    return {
      url: "/settings/devices/allowBlockList",
      method: "POST",
      data,
    };
  },
  uploadEdrAllowBlockListCSV: (data: FormData) => {
    return {
      url: "/settings/devices/allowBlockList/upload",
      method: "POST",
      data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  edrAllowBlockListCSVTemplate: {
    url: "/settings/devices/allowBlockList/upload/template",
  },
  getExecutiveReport: () => {
    return {
      url: "/summary-reports/workspace",
    };
  },
  getDnsReport: () => {
    return {
      url: "/settings/swg/dns/report",
    };
  },
  getDnsReportDomains: () => {
    return {
      url: "/settings/swg/dns/report-domains",
      method: "POST",
    };
  },
  getDnsFilters: () => ({
    url: "/settings/swg/dns/report/filters",
  }),
  getManagedServicesReport: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/summary-reports/soc",
      params: {
        fromTime,
        toTime,
      },
    };
  },

  getPhishingSimulationReport: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/summary-reports/security-awareness/simulations",
      params: {
        fromTime,
        toTime,
      },
    };
  },
  getPhishingSimulationReportUsers: () => {
    return {
      url: "/summary-reports/security-awareness/simulations/users/search",
      method: "POST",
    };
  },
  getSecuredMessagesReport: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/summary-reports/secured-messages",
      params: {
        fromTime,
        toTime,
      },
    };
  },
  getWarningOnlyEmailEventsReport: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/summary-reports/warning-email-events",
      params: {
        fromTime,
        toTime,
      },
    };
  },
  getSecurityAwarenessReport: ({ fromTime, toTime }: UnixTimeFrame) => {
    return {
      url: "/summary-reports/security-awareness/trainings",
      params: {
        fromTime,
        toTime,
      },
    };
  },
  getSecurityAwarenessReportUsers: () => {
    return {
      url: "/summary-reports/security-awareness/trainings/users/search",
      method: "POST",
    };
  },
  exportSecurityTrainingToCsv: () => {
    return {
      url: "/summary-reports/trainings/export/csv",
    };
  },
  exportWarningEmailEventsToCsv: () => {
    return {
      url: "/summary-reports/warning-email-events/export/csv",
    };
  },
  getMspWarningOnlyEmailEventsReport: (
    { fromTime, toTime }: UnixTimeFrame,
    workspaceLimit: number
  ) => {
    return {
      url: "/msp/reports/workspaces/email-warnings",
      params: {
        fromTime,
        toTime,
        workspaceLimit,
      },
    };
  },
  exportMspWarningEmailEventsToCsv: {
    url: "/msp/exports/workspaces/email-warnings",
    method: "POST",
  },
  uploadPdfReport: {
    url: "/summary-reports/upload",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    method: "POST",
  },
  getRolesList: () => {
    return {
      url: "/roles",
      method: "GET",
    };
  },
  getRolePermissions: (id: string) => {
    return {
      url: `/roles/${id}/permissions`,
      method: "GET",
    };
  },
  getRoleAssignees: (id: string, { page, pageSize }: Pagination) => {
    return {
      url: `/roles/${id}/assignees`,
      method: "GET",
      params: {
        page,
        pageSize,
      },
    };
  },
  createNewRole: (data: RolePermissions) => {
    return {
      url: `/roles`,
      method: "POST",
      data,
    };
  },
  deleteRole: (id: string) => {
    return {
      url: `/roles/${id}`,
      method: "DELETE",
    };
  },
  editRole: (id: string, data: RolePermissions) => {
    return {
      url: `/roles/${id}`,
      method: "PUT",
      data,
    };
  },
  searchRoles: () => {
    return {
      url: "/roles/search",
      method: "GET",
    };
  },
  getMspRolesList: () => {
    return {
      url: "/roles/msp",
      method: "GET",
    };
  },
  getMspRolePermissions: (id: string) => {
    return {
      url: `/roles/msp/${id}/permissions`,
      method: "GET",
    };
  },
  getMspRoleAssignees: (id: string, { page, pageSize }: Pagination) => {
    return {
      url: `/roles/msp/${id}/assignees`,
      method: "GET",
      params: {
        page,
        pageSize,
      },
    };
  },
  createNewMspRole: (data: RolePermissions) => {
    return {
      url: `/roles/msp`,
      method: "POST",
      data,
    };
  },
  deleteMspRole: (id: string) => {
    return {
      url: `/roles/msp/${id}`,
      method: "DELETE",
    };
  },
  editMspRole: (id: string, data: RolePermissions) => {
    return {
      url: `/roles/msp/${id}`,
      method: "PUT",
      data,
    };
  },
  searchMspRoles: () => {
    return {
      url: "/roles/msp/search",
      method: "GET",
    };
  },
  getGlobalRolesList: () => {
    return {
      url: "/roles/global",
      method: "GET",
    };
  },
  getGlobalRolePermissions: (id: string) => {
    return {
      url: `/roles/global/${id}/permissions`,
      method: "GET",
    };
  },
  getGlobalRoleAssignees: (id: string, { page, pageSize }: Pagination) => {
    return {
      url: `/roles/global/${id}/assignees`,
      method: "GET",
      params: {
        page,
        pageSize,
      },
    };
  },
  createNewGlobalRole: (data: RolePermissions) => {
    return {
      url: `/roles/global`,
      method: "POST",
      data,
    };
  },
  deleteGlobalRole: (id: string) => {
    return {
      url: `/roles/global/${id}`,
      method: "DELETE",
    };
  },
  editGlobalRole: (id: string, data: RolePermissions) => {
    return {
      url: `/roles/global/${id}`,
      method: "PUT",
      data,
    };
  },
  searchGlobalRoles: () => {
    return {
      url: "/roles/global/search",
      method: "GET",
    };
  },
  emailsSettings: () => ({
    url: "/settings/emails",
  }),
  resetEmailFileTypes: () => ({
    url: "/settings/emails/resetFileTypes",
  }),
  searchEmailUsers: (email: string) => ({
    url: `/users/search?email=${email}`,
  }),
  emailProxyDomains: () => {
    return {
      url: "/settings/emailProxy",
    };
  },
  editEmailProxyDomain: (id: string) => {
    return {
      url: `/settings/emailProxy/${id}`,
    };
  },
  testEmailProxyDomain: (id: string) => {
    return {
      url: `/settings/emailProxy/${id}/testProxy`,
      method: "POST",
    };
  },
  completeEmailProxyTest: (proxyDomainId: string) => {
    return {
      url: `/emailProxy/${proxyDomainId}/testProxy/check`,
      method: "POST",
    };
  },
  getEmailProxyStatus: () => {
    return {
      url: "/settings/emailProxy/proxyStatus",
    };
  },
  getOutboundProxyVerificationEmail: () => {
    return {
      url: "/settings/emailProxy/outboundDomainVerification",
    };
  },
  getNgavSettings: () => ({
    url: "/settings/devices/ngav",
  }),
  importAllowBlockList: () => {
    return {
      url: "/settings/phishing/list/csv",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  getPhishingAllowBlackListExampleCsv: () => {
    return {
      url: "/settings/phishing/list/csv/example",
    };
  },
  getPhishingAllowBlackList: () => {
    return {
      url: "/settings/phishing/list",
    };
  },
  validatePhishingAllowBlockList: () => {
    return {
      url: "/settings/phishing/list/validate",
      method: "POST",
    };
  },
  getPhishingExistingTlds: () => {
    return {
      url: "/settings/phishing/list/tlds/existing",
    };
  },
  getPhishingRestrictedTlds: () => {
    return {
      url: "/settings/phishing/list/tlds/restricted",
    };
  },
  getGlobalAllowBlockList: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list",
    };
  },
  validateGlobalAllowBlockList: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list/validate",
      method: "POST",
    };
  },
  getGlobalAllowBlockListExistingTlds: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list/tlds/existing",
    };
  },
  getGlobalAllowBlockListRestrictedTlds: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list/tlds/restricted",
    };
  },
  importGlobalAllowBlockList: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list/csv",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  getGlobalAllowBlockListExampleCsv: () => {
    return {
      url: "/backoffice/email-security/sender-allow-list/csv/example",
    };
  },
  getDevicesWifiPhishingSettings: () => ({
    url: "/settings/devices/wifiPhishing",
  }),
  getActiveSessions({ page, pageSize }: Pagination) {
    return {
      url: "/sessions",
      params: {
        page,
        pageSize,
      },
    };
  },
  revokeSession: (sessionId: string) => {
    return {
      url: `/sessions/revoke/${sessionId}`,
      method: "PUT",
    };
  },
  services: {
    url: "/dashboard/services",
  },
  cloudAppServiceHeartbeat: () => {
    return {
      url: "/dashboard/services/heartbeat",
    };
  },
  protectedUsersCount: (serviceName: string) => {
    return {
      url: `/services/${serviceName}/protected-users/count`,
    };
  },
  protectAllUsers: (serviceName: string) => {
    return {
      url: `/services/${serviceName}/users/protect`,
      method: "POST",
      data: {},
    };
  },
  connectService: () => {
    return {
      url: "/services/action/connect",
    };
  },
  grantPermissionsForService: () => {
    return {
      url: "/services/action/grantPermissions",
    };
  },
  completeOnboarding: {
    url: "/services/signup/complete",
    method: "POST",
  },
  disconnectService: (service: string) => {
    return {
      url: `/services/action/disconnect?service=${service}`,
      method: "post",
      data: {},
    };
  },
  uploadBoxConfiguration: (data: FormData) => {
    return {
      url: "/services/box/configUpload",
      data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      method: "POST",
    };
  },
  cloudSecuritySettings: () => {
    return {
      url: "/settings/cloud-security",
    };
  },
  thirdPartyApps: () => {
    return {
      url: "/services/thirdPartyApps",
    };
  },
  toggleThirdPartyAppsStatus: (id: number) => {
    return {
      url: `/services/thirdPartyApps/toggle/${id}`,
    };
  },
  thirdPartyAppsPermissions: () => {
    return {
      url: `/services/thirdPartyApps/cloudServicePermissions`,
    };
  },
  threatTypes: () => {
    return {
      url: "/threat-types",
    };
  },
  restrictions: () => {
    return {
      url: "/restrictions",
    };
  },
  ipBlocklist: () => {
    return {
      url: "/ip-blocklist",
    };
  },
  users: () => {
    return {
      url: "/users",
    };
  },
  getProtectedUsersCount: () => {
    return {
      url: "/services/protected-users/count",
    };
  },
  userProtectionStatus: () => {
    return {
      url: "/services/protected-users/protection",
    };
  },
  syncCloudServices: () => {
    return {
      url: "/services/sync",
    };
  },
  usersProtectionStatusServices: () => {
    return {
      url: "/services/protected-users/cloud-protection",
    };
  },
  searchGroups: (name: string, service?: Service | "all", limit = 10) => {
    const params = {
      name,
      limit,
      service: service === "all" ? null : service,
    };

    return {
      url: "/groups/search",
      params,
    };
  },
  completeServiceConnection: () => {
    return {
      url: "/services/action/completeConnection",
      method: "put",
    };
  },
  completePermissionsGranting: () => {
    return {
      url: "/services/action/completePermissionsGranting",
      method: "put",
    };
  },
  fetchGoogleClientId: () => ({
    url: "/services/googleClientId",
  }),
  activateMobile: (mobileToken: string, workspaceId: string) => {
    return {
      url: "/mobile/activate",
      method: "GET",
      params: { mobileToken },
      headers: {
        Workspace: workspaceId,
      },
    };
  },
  resendMobileActivationLink: (workspaceId: string) => {
    return {
      url: "/mobile/resendInvite",
      method: "POST",
      headers: {
        Workspace: workspaceId,
      },
    };
  },
  devicesSettings: {
    url: "/settings/devices",
  },
  devicesAgentVersions: {
    url: "/settings/devices/agentVersions",
  },
  devicesLabels: {
    url: "/devices/labels",
  },
  devicesLabel: (id: string) => ({
    url: `/devices/labels/${id}`,
  }),
  downloadClientPageLink: {
    url: "/settings/devices/downloadClientPageLink",
  },
  devices: () => {
    return {
      url: "/devices",
    };
  },
  getDevice: (enrollmentCode: string) => {
    return {
      url: `/devices/${enrollmentCode}`,
      method: "GET",
    };
  },
  getDeviceTickets: (enrollmentCode: string) => {
    return {
      url: `/devices/${enrollmentCode}/tickets`,
      method: "GET",
    };
  },
  getDeviceActivityLogs: (enrollmentCode: string) => {
    return {
      url: `/devices/${enrollmentCode}/activityLogs`,
      method: "GET",
    };
  },
  getDevicesLogs: (deviceId: string) => {
    return {
      url: `/devices/logs/${deviceId}`,
    };
  },
  devicesAction: {
    url: "/devices/action",
    method: "POST",
  },
  devicesProtectionLimit: {
    url: "/devices/protectionLimit",
    method: "GET",
  },
  mspDevicesProtectionLimit: {
    url: "/msp/devices/protectionLimit",
    method: "GET",
  },
  devicesOsVersions: {
    url: "devices/os/versions",
  },
  devicesClientVersions: {
    url: "devices/client/versions",
  },
  devicesStatuses: {
    url: "devices/group/statuses",
  },
  deviceAdvancedInfo: (enrollmentCode: string) => {
    return {
      url: `/devices/${enrollmentCode}/advancedInfo`,
    };
  },
  mergeDuplicateDevices: (enrollmentCode: string) => {
    return {
      url: `/mobile/${enrollmentCode}/merge`,
      method: "POST",
    };
  },
  ignoreDuplicateDevicesMerge: (enrollmentCode: string) => {
    return {
      url: `/mobile/${enrollmentCode}/ignore-merge`,
      method: "PUT",
    };
  },

  getTerminalSupportedCommands: (enrollmentCode: string) => {
    return {
      url: `/devices/ssh/supportedCommands/${enrollmentCode}`,
    };
  },

  sendTerminalCommand: (enrollmentCode: string) => {
    return {
      url: `/devices/ssh/${enrollmentCode}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  getSupportedCommands: (enrollmentCode: string) => {
    return {
      url: `/devices/ssh/supportedCommands/${enrollmentCode}`,
    };
  },
  getLastCommandResults: (commandId: number, receivedAfter: number | null) => {
    const params: { receivedAfter?: number } = {};
    if (receivedAfter) {
      params.receivedAfter = receivedAfter + 1;
    }
    return {
      url: `/devices/ssh/command/${commandId}`,
      params,
    };
  },
  getSessionStatus: (sessionId: number) => {
    return {
      url: `/devices/ssh/session/${sessionId}`,
    };
  },
  mspDevices: () => {
    return {
      url: "/msp/devices",
    };
  },
  getMspDevice: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}?workspaceId=${workspaceId}`,
      method: "GET",
    };
  },
  getMspDeviceTickets: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/tickets?workspaceId=${workspaceId}`,
      method: "GET",
    };
  },
  getMspDeviceActivityLogs: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/activity-logs?workspaceId=${workspaceId}`,
      method: "GET",
    };
  },
  mspDeviceLabels: {
    url: "/msp/devices/labels",
    method: "GET",
  },
  mspDevicesAction: {
    url: "/msp/devices/action",
    method: "POST",
  },
  mspDevicesOsVersions: {
    url: "/msp/devices/os/versions",
  },
  mspDevicesClientVersions: {
    url: "/msp/devices/client/versions",
  },
  mspDevicesStatuses: {
    url: "/msp/devices/group/statuses",
  },
  mspDeviceAdvancedInfo: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/advanced-info?workspaceId=${workspaceId}`,
    };
  },
  mspMergeDuplicateDevices: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/merge?workspaceId=${workspaceId}`,
      method: "POST",
    };
  },
  mspIgnoreDuplicateDevicesMerge: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/ignore-merge?workspaceId=${workspaceId}`,
      method: "PUT",
    };
  },
  getMspTerminalSupportedCommands: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/${enrollmentCode}/ssh/supported-commands?workspaceId=${workspaceId}`,
    };
  },
  sendMspTerminalCommand: (enrollmentCode: string, workspaceId: string) => {
    return {
      url: `/msp/devices/ssh/${enrollmentCode}?workspaceId=${workspaceId}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  getMspLastCommandResults: (
    commandId: number,
    receivedAfter: number | null,
    workspaceId: string
  ) => {
    const params: { receivedAfter?: number } = {};
    if (receivedAfter) {
      params.receivedAfter = receivedAfter + 1;
    }
    return {
      url: `/msp/devices/ssh/command/${commandId}?workspaceId=${workspaceId}`,
      params,
    };
  },
  getMspSessionStatus: (sessionId: number, workspaceId: string) => {
    return {
      url: `/msp/devices/ssh/session/${sessionId}?workspaceId=${workspaceId}`,
    };
  },
  getEdrTelemetryList: (data: GetEdrTelemetryListParams) => {
    return {
      url: "/edr/telemetry",
      method: "POST",
      data,
    };
  },
  getEdrLog: (id: string) => {
    return {
      url: `/edr/telemetry/${id}/fullLog`,
    };
  },
  getRemoteShellSessionLog: (logId: string) => {
    return {
      url: `/devices/ssh/session/log/${logId}`,
    };
  },
  processTelemetryAction: (id: string) => {
    return {
      url: `/edr/telemetry/${id}/action`,
      method: "post",
    };
  },
  getEdrTelemetryDetails: (id: string) => {
    return {
      url: `/edr/telemetry/${id}`,
    };
  },
  edrTelemetryCsvExport: {
    url: "/edr/telemetry/export/csv",
    method: "POST",
  },
  getEdrProcessList: (params: GetEdrProcessesListParams) => {
    return {
      url: "/edr/processes",
      params,
    };
  },
  getEdrProcessDetails: (id: string) => {
    return {
      url: `/edr/processes/${id}`,
    };
  },
  getEdrProcessPaths: (id: string, params: Pagination) => {
    return {
      url: `/edr/processes/${id}/paths`,
      params,
    };
  },
  getEdrProcessAliases: (id: string, params: Pagination) => {
    return {
      url: `/edr/processes/${id}/aliases`,
      params,
    };
  },
  getEdrProcessTickets: (id: string) => {
    return {
      url: `/edr/processes/${id}/openTickets`,
    };
  },
  processEdrAction: () => {
    return {
      url: "/edr/processes/action",
      method: "post",
    };
  },
  edrSettings: {
    url: "/edr/settings",
  },
  devicesCsvExport: {
    url: "/devices/csv/export",
    method: "POST",
  },
  mspDevicesCsvExport: {
    url: "/devices/csv/export",
    method: "POST",
  },
  groupsTotal: {
    url: "/groups/total",
  },
  exceptions: () => {
    return {
      url: "/users",
    };
  },
  protectedUsers: (email: string, pagination: Pagination) => {
    return {
      url: "/users",
      params: {
        isExcepted: false,
        email,
        page: pagination.page,
        pageSize: pagination.pageSize,
      },
    };
  },
  searchUsers: (email: string, limit = 10) => {
    return {
      url: "/users/cloud/search",
      params: {
        email,
        limit,
      },
    };
  },
  addUsers: {
    url: "/users",
    method: "post",
  },
  syncUsers: {
    url: "/users/sync",
    method: "post",
  },
  removeUser: ({ userIds }: { userIds: string[] }) => {
    return {
      url: "/users",
      method: "delete",
      data: {
        userIds,
      },
    };
  },
  addAllUsersToProtection: (service: string) => {
    return {
      url: "/users/addAllToProtection",
      method: "post",
      data: {
        service,
      },
    };
  },
  protectFromReport: (startTime: number, endTime: number) => {
    return {
      url: `/users/protectFromReport?startTime=${startTime}&endTime=${endTime}`,
      method: "POST",
    };
  },
  addExceptions: {
    url: "/users/exceptions",
    method: "post",
  },
  removeException: (userId: string) => ({
    url: "/users/exceptions",
    params: {
      userId,
    },
    method: "delete",
  }),
  importUsersViaCsv: (data: FormData) => {
    return {
      url: "/users/csv",
      data,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
  },
  getUsersExampleCsv: () => {
    return {
      url: "/users/csv/example",
      method: "GET",
    };
  },
  groups: (
    name: string,
    pagination: Pagination,
    protectionStatus?: GroupProtectionStatus | null
  ) => {
    return {
      url: "/groups",
      params: {
        name,
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...(protectionStatus ? { protectionStatus } : {}),
      },
    };
  },
  addGroups: {
    url: "/groups",
    method: "post",
  },
  removeGroups: {
    url: "/groups",
    method: "delete",
  },
  updateGroupProtection: {
    url: "/services/protected-groups/protection",
    method: "put",
  },
  exportUsersCsv: {
    url: "/users/csv/export",
    method: "post",
  },
  exportDnsDomainsCsv: () => {
    return {
      url: "/settings/swg/dns/report-domains/csv/export",
      method: "post",
    };
  },
  mspWorkspaces: () => {
    return {
      url: "/workspaces/msp",
    };
  },
  mspWorkspace: (id: string) => {
    return {
      url: `/workspaces/msp/${id}`,
    };
  },
  exportMspWorkspaces: () => {
    return {
      url: "/msp/exports/workspaces?type=csv",
      method: "POST",
    };
  },
  exportMspNotifications: () => {
    return {
      url: "/msp/exports/workspaces/notifications?type=csv ",
      method: "POST",
    };
  },
  exportMspSummary: () => {
    return {
      url: "/msp/exports/workspaces/summary",
      method: "POST",
    };
  },
  exportMspTicketsSummaryToCsv: () => {
    return {
      url: "/msp/exports/tickets/summary",
      method: "POST",
    };
  },
  convertMspWorkspace: (id: string) => {
    return {
      url: `/workspaces/msp/${id}/type`,
    };
  },
  archiveWorkspace: (workspaceId: string) => {
    return {
      url: `/workspaces/msp/${workspaceId}/archive`,
      method: "PUT",
    };
  },
  unarchiveWorkspace: (workspaceId: string) => {
    return {
      url: `/workspaces/msp/${workspaceId}/unarchive`,
      method: "PUT",
    };
  },
  checkInvitedAdminAlreadyMspAdmin: () => {
    return {
      url: `/admin-users/msp/exists`,
      method: "POST",
    };
  },
  getRelevantHierarchy: () => {
    return {
      url: `/msp/hierarchy`,
    };
  },
  getHierarchies: (search: string, page: number, pageSize: number) => {
    return {
      url: `/msp/hierarchy/search`,
      params: {
        search,
        page,
        pageSize,
      },
    };
  },
  getWorkspacesToAssignPolicy: (
    filters: ApplyPolicyFilters,
    pagination: Pagination,
    assignedMspPolicyId: string
  ) => {
    const convertedFilters = {
      ...filters,
      policiesFilter: filters.policiesFilter.join(","),
    };
    return {
      url: `/msp/policy/assign/candidates`,
      params: {
        ...convertedFilters,
        ...pagination,
        assignedMspPolicyId,
      },
    };
  },
  assignGlobalPolicy: {
    url: `/msp/policy/assign`,
    method: "PUT",
  },
  getMspWorkspaceDescendants: () => {
    return {
      url: `/msp/hierarchy/descendants`,
    };
  },
  workspaceMspCandidates: () => {
    return {
      url: `/workspaces/msp/candidates`,
      method: "GET",
    };
  },
  bulkEditLabel: () => {
    return {
      url: `/msp/hierarchy/label/assign`,
    };
  },
  extendWorkspaceTrial: (workspaceId: string) => {
    return {
      url: `/workspaces/${workspaceId}/extendTrial`,
      method: "PUT",
    };
  },
  editSubscription: () => {
    return {
      url: `/workspaces/msp/subscription`,
      method: "PUT",
    };
  },
  updateSubscriptionType: () => {
    return {
      url: `/workspaces/subscriptionType`,
      method: "PUT",
    };
  },
  mspAdminUsers: () => ({
    url: "/admin-users/msp",
  }),
  mspAdminUser: (id: string) => ({
    url: `/admin-users/msp/${id}`,
  }),
  mspAdminUsersCandidates: () => ({
    url: `/admin-users/msp/candidates`,
  }),
  mspAdminUsersWorkspaces: () => ({
    url: `/admin-users/msp/workspaces`,
  }),
  getAgentGradualUpdateData: ({
    workspacesGroup,
    devicesGroup,
    betaGroupUpdate,
  }: Partial<AgentGradualUpdateData>) => {
    return {
      url: `/gradual-update`,
      params: {
        workspacesGroup,
        devicesGroup,
        betaGroupUpdate,
      },
    };
  },
  saveAgentGradualUpdateData: (data: Partial<AgentGradualUpdateData>) => {
    return {
      url: `/gradual-update`,
      method: "PUT",
      data,
    };
  },
  getExportsList: () => {
    return {
      url: "/portal/account/exports-list",
    };
  },
  exportTicketsToCsv: () => {
    return {
      url: "/events/export/csv",
      method: "POST",
    };
  },
  mspConversionWorkplaceCandidates: ({
    name,
    page,
    pageSize,
    type,
    channelWorkspaceIdToExclude,
  }: {
    name: string;
    page: number;
    pageSize: number;
    type?: string;
    channelWorkspaceIdToExclude?: string;
  }) => {
    return {
      url: "/workspaces/msp/conversion/parent-candidates",
      params: {
        name,
        page,
        pageSize,
        type,
        channelWorkspaceIdToExclude,
      },
    };
  },
  getDevicesPostureRegularSettings: {
    url: "/settings/devices/posture/labeled",
  },
  updateDevicePostureRegularSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/labeled/${id}`,
      method: "PUT",
    };
  },
  addDevicePostureRegularSetting: () => {
    return {
      url: "/settings/devices/posture/labeled",
      method: "POST",
    };
  },
  getDeviceInfo: (enrollmentCode: string) => {
    return {
      url: `/devices/${enrollmentCode}`,
    };
  },
  deleteDevicePostureSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/labeled/${id}`,
      method: "DELETE",
    };
  },
  updateDevicePostureWifiSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/wifi/${id}`,
      method: "PUT",
    };
  },
  addDevicePostureWifiSetting: () => {
    return {
      url: "/settings/devices/posture/wifi",
      method: "POST",
    };
  },
  getDevicesPostureWifiSettings: {
    url: "/settings/devices/posture/wifi",
  },
  getDevicesPostureUsbLockdownSettings: {
    url: "/settings/devices/posture/usbLockdown",
  },
  deleteDevicesPostureWifiSettings: (id: string) => {
    return {
      url: `/settings/devices/posture/wifi/${id}`,
      method: "DELETE",
    };
  },
  updateDevicePostureSensitiveScanSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/dlp/scanSchedule/${id}`,
      method: "PUT",
    };
  },
  addDevicePostureSensitiveScanSetting: () => {
    return {
      url: "/settings/devices/posture/dlp/scanSchedule",
      method: "POST",
    };
  },
  deleteDevicesSensitiveScanSettings: (id: string) => {
    return {
      url: `/settings/devices/posture/dlp/scanSchedule/${id}`,
      method: "DELETE",
    };
  },
  updateDevicePostureUserAccountSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/userAccountPolicy/${id}`,
      method: "PUT",
    };
  },
  updateDevicePostureUsbLockdownSetting: (id: string) => {
    return {
      url: `/settings/devices/posture/usbLockdown/${id}`,
      method: "PUT",
    };
  },
  addDevicesPostureUsbLockdownSettings: () => {
    return {
      url: "/settings/devices/posture/usbLockdown",
      method: "POST",
    };
  },
  addDevicePostureUserAccountSetting: () => {
    return {
      url: "/settings/devices/posture/userAccountPolicy",
      method: "POST",
    };
  },
  getDevicesPostureUserAccountSettings: {
    url: "/settings/devices/posture/userAccountPolicy",
  },
  deleteDevicesPostureUserAccountSettings: (id: string) => {
    return {
      url: `/settings/devices/posture/userAccountPolicy/${id}`,
      method: "DELETE",
    };
  },
  deleteDevicesPostureUsbLockdownSettings: (id: string) => {
    return {
      url: `/settings/devices/posture/usbLockdown/${id}`,
      method: "DELETE",
    };
  },
  getDevicePolicies: {
    url: "/settings/devices/devicePolicies/",
  },
  addDevicePolicy: (policyType: DevicePolicyType) => ({
    url: `/settings/devices/devicePolicies/${policyType}`,
    method: "POST",
  }),
  updateDevicePolicy: (policyType: DevicePolicyType, id: string) => ({
    url: `/settings/devices/devicePolicies/${policyType}/${id}`,
    method: "POST",
  }),
  deleteDevicePolicy: (policyType: DevicePolicyType, id: string) => ({
    url: `/settings/devices/devicePolicies/${policyType}/${id}`,
    method: "DELETE",
  }),
  getSwgSettings: {
    url: "/settings/swg",
  },
  sideToSideTunnels: {
    url: "/settings/addons/network/site-to-site-tunnels",
  },
  siteToSiteTunnel: (id: string | number) => {
    return {
      url: `/settings/addons/network/site-to-site-tunnels/${id}`,
    };
  },
  testSideToSideTunnels: (id: string | number) => {
    return {
      url: `/settings/addons/network/site-to-site-tunnels/${id}/test`,
      method: "POST",
    };
  },

  validateSubnetRange: (data: string[]) => ({
    url: `/settings/addons/network/site-to-site-tunnels/validate`,
    method: "POST",
    data,
  }),

  toggleSiteToSiteTunnel: (id: string | number, enable: boolean) => {
    return {
      url: `/settings/addons/network/site-to-site-tunnels/${id}/toggle`,
      method: "PUT",
      params: {
        enable,
      },
    };
  },
  downloadTunnelLogs: (tunnelId: string | number) => {
    return {
      url: `/settings/addons/network/site-to-site-tunnels/${tunnelId}/download`,
      method: "GET",
    };
  },
  rocSettings: {
    url: "/settings/addons/network/site-to-site-tunnels/rocs",
  },
  networkAnalyticsFeatures: {
    url: "/settings/addons/network/analytics/features",
    // The Network Analytics navigation guard awaits this before it calls next(), and the shared axios
    // instance sets no timeout at all. Without one, a stalled request never settles and the navigation
    // never completes, which reads to the user as the Network tile doing nothing at all.
    timeout: 15000,
  },
  networkAnalyticsSummary: ({ fromTime, toTime }: UnixTimeFrame) => ({
    url: "/settings/addons/network/analytics/summary",
    params: { fromTime, toTime },
  }),
  networkAnalyticsMap: ({ fromTime, toTime }: UnixTimeFrame) => ({
    url: "/settings/addons/network/analytics/map",
    params: { fromTime, toTime },
  }),
  networkAnalyticsRegions: ({ fromTime, toTime }: UnixTimeFrame) => ({
    url: "/settings/addons/network/analytics/regions",
    params: { fromTime, toTime },
  }),
  networkAnalyticsTimeseries: ({
    fromTime,
    toTime,
    granularity,
  }: UnixTimeFrame & { granularity: string }) => ({
    url: "/settings/addons/network/analytics/timeseries",
    params: { fromTime, toTime, granularity },
  }),
  networkAnalyticsConnections: (
    params: UnixTimeFrame &
      Pagination & {
        sort?: string;
        search?: string | null;
        status?: string | null;
      }
  ) => ({
    url: "/settings/addons/network/analytics/connections",
    params,
  }),
  networkAnalyticsDestinations: (
    connectionId: string,
    params: UnixTimeFrame & Pagination & { sort?: string }
  ) => ({
    url: `/settings/addons/network/analytics/connections/${connectionId}/destinations`,
    params,
  }),
  revokeNetworkAnalyticsConnection: (connectionId: string) => ({
    url: `/settings/addons/network/analytics/connections/${connectionId}/revoke`,
    method: "POST",
  }),
  connectNetworkAnalyticsConnection: (connectionId: string) => ({
    url: `/settings/addons/network/analytics/connections/${connectionId}/connect`,
    method: "POST",
  }),
  vpnSetup: {
    url: "/settings/addons/network/vpn/setup",
  },
  retryVirtualOffice: {
    url: "/settings/addons/network/vpn/setup/retry",
    method: "POST",
  },
  vpnRegions: {
    url: "/settings/addons/network/vpn/regions",
  },
  setupVirtualOffice: (data: { region: string; subnet: string }) => {
    return {
      url: "/settings/addons/network/vpn/setup",
      method: "POST",
      data,
    };
  },
  networkSettings: () => {
    return { url: `/settings/addons/network/vpn/settings-common` };
  },
  networkModuleSettings: () => {
    return { url: `/settings/addons/network/vpn/settings` };
  },
  getVpnSettings: () => ({
    url: "/settings/addons/network/vpn",
    method: "GET",
  }),
  getMobileBannerConfiguration: () => ({
    url: "/settings/addons/network/vpn/mobile-banner",
    method: "GET",
  }),
  updateMobileBannerConfiguration: (data: { visible: boolean }) => ({
    url: "/settings/addons/network/vpn/mobile-banner",
    method: "PUT",
    data,
  }),
  getVpnUrlList: ({
    page,
    pageSize,
    search,
    excluded,
  }: {
    page: number;
    pageSize: number;
    search?: string | null;
    excluded?: boolean;
  }) => ({
    url: "/settings/addons/network/vpn/urls",
    method: "GET",
    params: {
      page,
      pageSize,
      search,
      excluded,
    },
  }),
  removeFromVpnUrlList: (data: { urlName: string; excluded: boolean }) => ({
    url: "/settings/addons/network/vpn/urls",
    method: "DELETE",
    data,
  }),
  addToVpnUrlList: (data: { items: string[]; excluded: boolean }) => ({
    url: "/settings/addons/network/vpn/urls",
    method: "POST",
    data,
  }),
  trustedNetworks: {
    url: "/settings/addons/network/trusted-networks",
  },
  trustedNetworkAction: {
    url: "/settings/addons/network/trusted-networks/action",
  },
  updateVpnSettings: (data: VirtualOfficeSettings) => ({
    url: "/settings/addons/network/vpn",
    method: "PUT",
    data,
  }),
  swgUrlList: () => ({
    url: "/settings/swg/urls",
  }),
  customDomainDns: () => ({
    url: "/settings/swg/dns",
  }),
  conditionalForwarding: () => ({
    url: "/settings/swg/conditional-forwarding",
  }),
  conditionalForwardingById: (id: string) => ({
    url: `/settings/swg/conditional-forwarding/${id}`,
  }),
  importToSwgUrlList: (data: FormData) => ({
    url: "/settings/swg/urls-file",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    method: "POST",
    data,
  }),
  swgUrlListItem: (id: string) => ({
    url: `/settings/swg/urls/${id}`,
  }),
  swgUrlListItemLabels: (id: string) => ({
    url: `/settings/swg/urls/${id}/labels`,
  }),
  updateSwgSettings: (data: SecureWebGatewaySettings) => ({
    url: "/settings/swg",
    method: "PUT",
    data,
  }),
  getSwgExampleFile: () => ({
    url: "/settings/swg/urls/example",
  }),
  swgUrlsFileValidateUnique: (name: string) => ({
    url: "/settings/swg/urls-file/validate-unique",
    params: { name },
  }),
  swgUrlValidateUnique: (urls: string[], allowed: boolean) => ({
    url: "/settings/swg/urls/validate-unique",
    method: "POST",
    data: { urls, allowed },
  }),
  viewUrlList: (id: string) => {
    return {
      url: `/settings/swg/urls/${id}/url-list`,
      method: "GET",
    };
  },
  getTelemetryRecentSearches: {
    url: "/edr/telemetry/recentSearches",
  },
  clearTelemetryRecentSearches: {
    url: "/edr/telemetry/recentSearches",
    method: "DELETE",
  },
  scheduledReports: () => ({
    url: "/scheduled-reports",
  }),
  scheduledReport: (id: string) => ({
    url: `/scheduled-reports/${id}`,
  }),
  billingStats: () => ({
    url: "/workspaces/billing/stats",
  }),
  updateBillingSubscription: () => ({
    url: "/workspaces/billing/update-subscription",
    method: "PUT",
  }),
  messages: {
    url: "/messages",
  },
  dismissMessage: (id: string) => {
    return {
      url: "/messages/dismiss",
      method: "POST",
      params: {
        id,
      },
    };
  },
  forgotPassword: (email: string, brandingAlias?: string) => {
    return {
      url: "/forgotPassword",
      params: {
        email,
        brandingAlias,
      },
    };
  },
  resetPassword: {
    url: "/resetPassword",
    method: "POST",
  },
  mdmUrl: {
    url: "/mdm/url",
  },
  reportPhishingImpersonation: () => {
    return {
      url: "/settings/phishing/report/impersonation",
      method: "POST",
    };
  },
  verifyMFACode: {
    url: "/mfa/verify-code",
    method: "POST",
  },
  mfaConfig: {
    url: "/portal/account/auth/mfa",
    method: "POST",
  },
  completeMFA: (verificationCode: string) => {
    return {
      url: "/portal/account/auth/mfa/complete",
      params: {
        verificationCode,
      },
      method: "POST",
    };
  },
  completeEmailVerificationMfa: (emailVerificationToken: string) => {
    return {
      url: "/desktop/activate-email",
      method: "GET",
      params: {
        emailVerificationToken,
      },
    };
  },
  demoMode: {
    url: "/demo-mode",
  },
  resetDemoModeData: {
    url: "/demo-mode/reset",
    method: "POST",
  },
  loginViaSSO: (token: string) => {
    return {
      url: "/auth/transfer",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  },
  contactUser: {
    url: "/users/contact",
    method: "post",
  },
  updateSocStatus: (eventId: string) => {
    return {
      url: `/soc/tickets/${eventId}/socStatus`,
      method: "PUT",
    };
  },
  searchSocWorkspaces: ({ search, page, pageSize }: Pagination & { search: string }) => {
    return {
      url: "/soc/tickets/workspaces",
      method: "GET",
      params: {
        search,
        page,
        pageSize,
      },
    };
  },
  getSocTickets: () => {
    return {
      url: "/soc/tickets",
    };
  },
  socTicket: (eventId: string) => {
    return {
      url: `/soc/tickets/${eventId}`,
    };
  },
  socTicketAction: (eventId: string) => {
    return {
      url: `/soc/tickets/${eventId}/action`,
      method: "post",
    };
  },
  getTickets: () => {
    return {
      url: "/events",
    };
  },
  ticket: (eventId: string) => ({
    url: `/events/${eventId}`,
  }),
  ticketAction: (eventId: string) => ({
    url: `/events/${eventId}/action`,
    method: "post",
  }),
  ticketBulkAction: () => {
    return {
      url: "/events/action",
      method: "post",
    };
  },
  getAvailableTicketBulkActions: () => ({
    url: "/events/actions/available",
    method: "POST",
  }),
  getTicketActivityLogs: (eventId: string) => {
    return {
      url: `/events/${eventId}/activityLogs`,
      method: "GET",
    };
  },
  getTicketRecipients: (eventId: string) => {
    return {
      url: `/events/${eventId}/recipients`,
      method: "GET",
    };
  },
  socTicketBulkAction: () => {
    return {
      url: "/soc/tickets/bulkAction",
      method: "post",
    };
  },
  edrAffectedDevices: (eventId: string, { page, pageSize }: Pagination) => {
    return {
      url: `/events/edr/${eventId}/affectedDevices`,
      params: {
        page,
        pageSize,
      },
    };
  },
  abnormalDataManipulationFiles: ({
    eventId,
    page,
    pageSize,
    folderId,
  }: Pagination & { eventId: string; folderId: string }) => {
    return {
      url: `/events/abnormalDataManipulation/${eventId}/${folderId}/files`,
      params: {
        page,
        pageSize,
      },
    };
  },
  findings: ({ eventId, page, pageSize }: Pagination & { eventId: string }) => {
    return {
      url: `/events/${eventId}/findings`,
      params: {
        page,
        pageSize,
      },
    };
  },
  dlpUnifiedFindings: ({
    eventId,
    page,
    pageSize,
    eventTrigger,
  }: Pagination & { eventId: string; eventTrigger: TicketTrigger }) => {
    return {
      url: `/events/${eventId}/filteredFindings`,
      params: {
        page,
        pageSize,
        eventTrigger,
      },
    };
  },
  dlpFindingsStructure: (eventId: string) => {
    return {
      url: `/events/${eventId}/findingsPerTrigger`,
    };
  },
  // @TODO Remove once BE will send a proper findings
  dlpFileNames: (eventId: string) => {
    return {
      url: `/events/${eventId}/fileNames`,
    };
  },
  abnormalDataManipulationFolders: ({
    eventId,
    page,
    pageSize,
  }: Pagination & { eventId: string }) => {
    return {
      url: `/events/abnormalDataManipulation/${eventId}/folders`,
      params: {
        page,
        pageSize,
      },
    };
  },
  coroAdminEmails: {
    url: "/admin-users/global/emails",
    method: "GET",
  },
  socCommentAction: (commentId: string) => {
    return {
      url: `/soc/tickets/comments/${commentId}/action`,
      method: "POST",
    };
  },
  addSocBulkComment: {
    url: "/soc/tickets/comments",
    method: "POST",
  },
  toggleZtnaEnabled: (enable: boolean) => ({
    url: "/settings/addons/network/ztna/toggle",
    method: "POST",
    params: { enable },
  }),
  ztnaRules: (id?: string) => {
    return {
      url: `/settings/addons/network/ztna/resources${id ? "/" + id : ""}`,
    };
  },
  ztnaStatus: () => {
    return {
      url: "/settings/addons/network/ztna/status",
    };
  },
  getProcessGraph: (params: GetProcessGraphParams) => {
    return {
      url: "/edr/process-tree",
      params,
    };
  },
  getProcessGraphDetails(id: string) {
    return {
      url: `/edr/process-tree/${id}`,
    };
  },
  processGraphAction(id: string, ticketId: string, action: ProcessGraphAction) {
    return {
      url: `/edr/process-tree/${id}/action`,
      method: "POST",
      data: {
        action,
        ticketId,
      },
    };
  },
  getBranding: (brandingAlias: string) => {
    return {
      url: `/branding/${brandingAlias}`,
    };
  },
  getMspUsers: () => {
    return {
      url: "/msp/users",
      method: "POST",
    };
  },
  getMspUserInfo: (userId: string) => {
    return {
      url: `/msp/users/${userId}`,
      method: "GET",
    };
  },
  getMspUserTickets: (userId: string) => {
    return {
      url: `/msp/users/${userId}/tickets`,
      method: "GET",
    };
  },
  getMspUserActivityLogs: (userId: string) => {
    return {
      url: `/msp/users/${userId}/activityLogs`,
      method: "GET",
    };
  },
  mspSingleUserAction: (userId: string) => ({
    url: `/msp/users/${userId}/actions/perform`,
    method: "POST",
  }),
  mspSingleUserActionsList: (userId: string) => ({
    url: `/msp/users/${userId}/actions/available`,
    method: "GET",
  }),
  mspUsersBulkAction: {
    url: "/msp/users/actions/perform",
    method: "POST",
  },
  mspUsersAvailableBulkActions: {
    url: "/msp/users/actions/available",
    method: "POST",
  },
  getMspUserAssociatedDevices: (userId: string) => {
    return {
      url: `/msp/users/${userId}/devices`,
      method: "GET",
    };
  },
  getMspTickets: () => {
    return {
      url: "/msp/tickets",
    };
  },
  mspTicket: (eventId: string) => ({
    url: `/msp/tickets/${eventId}`,
  }),
  mspTicketAction: (eventId: string) => ({
    url: `/msp/tickets/${eventId}/action`,
    method: "POST",
  }),
  mspTicketBulkAction: () => {
    return {
      url: "/msp/tickets/action",
      method: "POST",
    };
  },
  getMspTicketActivityLogs: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/activity-logs`,
      method: "GET",
    };
  },
  getMspTicketRecipients: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/recipients`,
      method: "GET",
    };
  },
  mspTicketEdrAffectedDevices: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/edr/affectedDevices`,
    };
  },
  mspTicketAbnormalDataManipulationFiles: ({
    eventId,
    folderId,
  }: {
    eventId: string;
    folderId: string;
  }) => {
    return {
      url: `/msp/tickets/${eventId}/abnormalDataManipulation/${folderId}/files`,
    };
  },
  mspTicketFindings: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/findings`,
    };
  },
  mspDlpUnifiedFindings: ({
    eventId,
    page,
    pageSize,
    eventTrigger,
    workspaceId,
  }: Pagination & { eventId: string; eventTrigger: TicketTrigger; workspaceId: string }) => {
    return {
      url: `/msp/tickets/${eventId}/filteredFindings`,
      params: {
        page,
        pageSize,
        eventTrigger,
        workspaceId,
      },
    };
  },
  mspDlpFindingsStructure: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/findingsPerTrigger`,
    };
  },
  // @TODO Remove once BE will send a proper findings
  mspDlpFileNames: (ticketId: string) => {
    return {
      url: `/msp/tickets/${ticketId}/fileNames`,
    };
  },
  mspTicketAbnormalDataManipulationFolders: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/abnormalDataManipulation/folders`,
    };
  },
  getMspTicketComments: (eventId: string) => {
    return {
      url: `/msp/tickets/${eventId}/comments`,
      method: "GET",
    };
  },
  exportMspTicketsToCsv: () => {
    return {
      url: "/msp/tickets/export/csv",
      method: "POST",
    };
  },
  getMspDrives: () => {
    return {
      url: "/msp/drives",
    };
  },
  mspDrivesAction: {
    url: "/msp/drives/action",
    method: "POST",
  },
  exportMspDevicesToCsv: () => {
    return {
      url: "/msp/devices/export/csv",
      method: "POST",
    };
  },
  exportMspUsersToCsv: () => {
    return {
      url: "/msp/users/export/csv",
      method: "POST",
    };
  },
  searchMspWorkspaceLabels: (search: string = "") => {
    return {
      url: `/msp/hierarchy/label/search`,
      method: "GET",
      params: {
        search,
      },
    };
  },
  backupAndRestoreStatus: {
    url: "/msp/backupAndRestore/status",
  },
  backupAndRestoreDescendants: {
    url: "/msp/backupAndRestore/descendants",
  },
  backupAndRestoreChildren: {
    url: "/msp/backupAndRestore/children",
  },
  backupAndRestoreDataCenters: {
    url: "/msp/backupAndRestore/dataCenters",
  },
  backupAndRestoreCreateChannel: {
    url: "/msp/backupAndRestore/createChannel",
    method: "POST",
  },
  backupAndRestoreCreateChild: {
    url: "/msp/backupAndRestore/createChild",
    method: "POST",
  },
  backupAndRestoreAutologin: {
    url: "/msp/backupAndRestore/autologin",
    method: "POST",
  },
  backupAndRestoreChildProperties: (childWorkspaceId: string) => ({
    url: `/msp/backupAndRestore/properties/${childWorkspaceId}`,
    method: "PUT",
  }),
  backupAndRestoreSingleViewConnectionStatus: {
    url: "/backupAndRestore/status",
  },
  backupAndRestoreConnectors: () => ({
    url: "/backupAndRestore/connectors",
    method: "GET",
  }),
  backupAndRestoreJobs: () => ({
    url: "/backupAndRestore/jobs",
    method: "GET",
  }),
  backupAndRestoreDashboard: () => ({
    url: "/dashboard/backupAndRestore",
    method: "GET",
  }),
  mspBackupAndRestoreDashboard: () => ({
    url: "/msp/dashboard/backupAndRestore",
    method: "GET",
  }),
  mspWorkspaceLabels: {
    url: "/msp/hierarchy/label",
  },
  mspWorkspaceLabel: (id: string) => ({
    url: `/msp/hierarchy/label/${id}`,
  }),
  mspWorkspaceDetails: (id: string) => ({
    url: `/workspaces/msp/${id}/details`,
  }),
  availableMspTicketBulkActions: {
    url: "/msp/tickets/available-actions",
    method: "POST",
  },
  verifyPublicEmail: (email: string) => ({
    url: `/sign-up/verify-email`,
    method: "POST",
    data: {
      email,
    },
  }),
  requestSignUpVerificationCode: () => ({
    url: `/sign-up/request-verification-code`,
  }),
  completeSignUp: () => ({
    url: `/sign-up/complete`,
  }),
  requestTrialForCurrentWorkspace: () => ({
    url: `/workspaces/current/request-trial`,
    method: "POST",
  }),
  resetTrialNotice: () => ({
    url: `/workspaces/current/reset-trial-notice`,
    method: "POST",
  }),
  mspDashboardGeneratedTickets: () => ({
    url: `/msp/dashboard/tickets/generated-stats`,
  }),
  mspDashboardModuleTickets: () => ({
    url: `/msp/dashboard/tickets/module-stats`,
  }),
  mspDashboardWorkspaceTickets: () => ({
    url: `/msp/dashboard/tickets/workspace-stats`,
  }),
  mspDashboardUsersStats: () => ({
    url: `/msp/dashboard/users/stats`,
  }),
  mspDashboardDevicesStats: () => ({
    url: `/msp/dashboard/devices/stats`,
  }),
  mspDashboardWorkspacesStats: () => ({
    url: `/msp/dashboard/workspaces/stats`,
  }),
  mspDashboardTopDevicesDelta: (from: number, subscriptionTypes: string) => ({
    url: `/msp/dashboard/workspaces/top-devices-delta`,
    params: {
      from,
      subscriptionTypes,
    },
  }),
  mspDashboardTopUsersDelta: (from: number, subscriptionTypes: string) => ({
    url: `/msp/dashboard/workspaces/top-users-delta`,
    params: {
      from,
      subscriptionTypes,
    },
  }),
  mspUsersAndDevicesTrend: (from: number, subscriptionTypes: string) => ({
    url: `/msp/dashboard/users-and-devices/range-stats`,
    params: {
      from,
      subscriptionTypes,
    },
  }),
  exportMspUsersAndDevicesTrend: (
    data: UsersAndDevicesTrendExportData & {
      subscriptionTypes: SubscriptionType[];
    }
  ) => ({
    url: `/msp/dashboard/users-and-devices/export/csv`,
    method: "POST",
    data,
  }),
  globalWorkspaces: () => {
    return {
      url: "/workspaces/global",
    };
  },
  globalWorkspace: (id: string) => {
    return {
      url: `/workspaces/global/${id}`,
    };
  },
  exportGlobalWorkspaces: () => {
    return {
      url: "/workspaces/global/export?type=csv",
      method: "POST",
    };
  },
  exportGlobalNotifications: () => {
    return {
      url: "/workspaces/global/notifications/export?type=csv ",
      method: "POST",
    };
  },
  exportGlobalMspSummary: () => {
    return {
      url: "/workspaces/global/summary/export",
      method: "POST",
    };
  },
  convertGlobalMspWorkspace: (id: string) => {
    return {
      url: `/workspaces/global/${id}/type`,
    };
  },
  archiveGlobalMspWorkspace: (workspaceId: string) => {
    return {
      url: `/workspaces/global/${workspaceId}/archive`,
      method: "PUT",
    };
  },
  unarchiveGlobalMspWorkspace: (workspaceId: string) => {
    return {
      url: `/workspaces/global/${workspaceId}/unarchive`,
      method: "PUT",
    };
  },
  startTrialForGlobalMspWorkspace: () => {
    return {
      url: `/workspaces/global/start-trial`,
      method: "POST",
    };
  },
  workspaceGlobalMspCandidates: () => {
    return {
      url: `/workspaces/global/candidates`,
      method: "GET",
    };
  },
  editGlobalWorkspaceSubscription: () => {
    return {
      url: `/workspaces/global/subscription`,
      method: "PUT",
    };
  },
  updateGlobalWorkspaceSubscriptionType: () => {
    return {
      url: `/workspaces/global/subscriptionType`,
      method: "PUT",
    };
  },
  globalConversionWorkplaceCandidates: ({
    name,
    page,
    pageSize,
    type,
    channelWorkspaceIdToExclude,
  }: {
    name: string;
    page: number;
    pageSize: number;
    type?: string;
    channelWorkspaceIdToExclude?: string;
  }) => {
    return {
      url: "/workspaces/global/conversion/parent-candidates",
      params: {
        name,
        page,
        pageSize,
        type,
        channelWorkspaceIdToExclude,
      },
    };
  },
  globalWorkspaceDetails: (id: string) => ({
    url: `/workspaces/global/${id}/details`,
  }),
  extendGlobalWorkspaceTrial: (workspaceId: string) => {
    return {
      url: `/workspaces/global/${workspaceId}/extendTrial`,
      method: "PUT",
    };
  },
  searchUserLabels: () => ({
    url: `/users/labels/search`,
  }),
  userLabels: () => ({
    url: `/users/labels`,
  }),
  userLabel: (id: string) => ({
    url: `/users/labels/${id}`,
  }),
  mspGlobalPolicy: () => ({
    url: `/msp/policy`,
  }),
  restoreGlobalPolicy: () => ({
    url: `/msp/policy/restore`,
    method: "PUT",
  }),
  mspCloudSecurityPolicy: (id: string) => ({
    url: `/msp/policy/${id}/cloud-security`,
  }),
  mspEmailSecurityPolicy: (id: string) => ({
    url: `/msp/policy/${id}/email-security`,
  }),
  mspGeneralInfoPolicy: (id: string) => ({
    url: `/msp/policy/${id}`,
  }),
  mspDeviceSettingsPolicy: (id: string) => ({
    url: `/msp/policy/${id}/devices`,
  }),
  mspEndpointDataGovernanceMonitoring: (id: string) => ({
    url: `/msp/policy/${id}/endpoint-data-governance/monitoring`,
  }),
  mspUserDataGovernanceMonitoring: (id: string) => ({
    url: `/msp/policy/${id}/user-data-governance/monitoring`,
  }),
  mspUserDataGovernance: (id: string) => ({
    url: `/msp/policy/${id}/user-data-governance`,
  }),
  mspEndpointDataGovernance: (id: string) => ({
    url: `/msp/policy/${id}/endpoint-data-governance`,
  }),
  mspEndpointSecurity: (id: string) => ({
    url: `/msp/policy/${id}/endpoint-security`,
  }),
  mspEndpointSecurityDevicePosture: (id: string) => ({
    url: `/msp/policy/${id}/device-posture`,
  }),
  mspEndpointSecurityAddons: (id: string) => ({
    url: `/msp/policy/${id}/addons`,
  }),
  mspEndpointSecurityEdr: (id: string) => ({
    url: `/msp/policy/${id}/edr`,
  }),
  mspUserDataGovernanceExclusions: (id: string) => ({
    url: `/msp/policy/${id}/user-data-governance/exclusions`,
  }),
  getMspGlobalPolicy: (id: string) => ({
    url: `/msp/policy/${id}`,
  }),
  searchSocContact: {
    url: "/settings/soc/candidates",
  },
  securityGaps: {
    url: "/settings/security-gaps",
  },
  mspSecurityGaps: {
    url: "/msp/settings/security-gaps",
  },
  getSecurityGapWorkspaces: {
    url: "/settings/security-gaps/workspaces",
  },
  getSecurityGapMspWorkspaces: {
    url: "/msp/settings/security-gaps/workspaces",
  },
  securityGapAction: {
    url: "/settings/security-gaps/action",
    method: "POST",
  },
  mspSecurityGapAction: {
    url: "/msp/settings/security-gaps/action",
    method: "POST",
  },
  securityGapRefresh: {
    url: "/settings/security-gaps/refresh",
    method: "POST",
  },
  mspSecurityGapRefresh: {
    url: "/msp/settings/security-gaps/refresh",
    method: "POST",
  },
  coroImpactVulnerableUsers: () => ({
    url: `/coro-impact/vulnerable-users`,
  }),
  coroImpactViolatorUsers: () => ({
    url: `/coro-impact/violator-users`,
  }),
  coroImpactTickets: () => ({
    url: `/coro-impact/tickets`,
  }),
  coroImpactAutoResolvedTickets: () => ({
    url: `/coro-impact/tickets/auto-resolved`,
  }),
  coroImpactSuspiciousDomains: () => ({
    url: `/coro-impact/suspicious-domains`,
  }),
  mspCoroImpactVulnerableUsers: () => ({
    url: `/msp/coro-impact/vulnerable-users`,
  }),
  mspCoroImpactViolatorUsers: () => ({
    url: `/msp/coro-impact/violator-users`,
  }),
  mspCoroImpactTickets: () => ({
    url: `/msp/coro-impact/tickets`,
  }),
  mspCoroImpactAutoResolvedTickets: () => ({
    url: `/msp/coro-impact/tickets/auto-resolved`,
  }),
  workspaceSubscriptionRules: (id: string) => ({
    url: `/workspaces/msp/${id}/subscription-rules`,
  }),
  mspExports: () => ({
    url: "/msp/exports",
  }),
  aiAssistantCreateSession: () => ({
    url: "/ai-assistant/sessions",
    method: "POST",
  }),
  aiAssistantSendMessage: (sessionId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}/messages`,
    method: "POST",
  }),
  aiAssistantGetMessageHistory: (sessionId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}/messages`,
  }),
  aiAssistantRefreshSuggestions: (sessionId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}/suggestions/refresh`,
    method: "POST",
  }),
  aiAssistantSubmitFeedback: (sessionId: string, messageId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}/messages/${messageId}/feedback`,
    method: "POST",
  }),
  aiAssistantGetSession: (sessionId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}`,
  }),
  approveAiToolExecution: (
    sessionId: string,
    approvalRequestId: string,
    toolExecutionRequestId: string
  ) => ({
    url: `/ai-assistant/sessions/${sessionId}/tool-approvals/${approvalRequestId}/tools/${toolExecutionRequestId}/approve`,
    method: "POST",
  }),
  rejectAiToolExecution: (
    sessionId: string,
    approvalRequestId: string,
    toolExecutionRequestId: string
  ) => ({
    url: `/ai-assistant/sessions/${sessionId}/tool-approvals/${approvalRequestId}/tools/${toolExecutionRequestId}/reject`,
    method: "POST",
  }),
  getToolApprovalMessage: (sessionId: string, approvalRequestId: string) => ({
    url: `/ai-assistant/sessions/${sessionId}/tool-approvals/${approvalRequestId}`,
    method: "GET",
  }),
  mspImportEmailSecurityAllowBlockList: () => ({
    url: "/msp/settings/allow-block-list/email-security/csv",
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }),
  mspEmailSecurityAllowBlockListExampleCSV: () => {
    return {
      url: "/msp/settings/allow-block-list/email-security/csv/example",
    };
  },
  mspEmailSecurityAllowBlockList: () => {
    return {
      url: "/msp/settings/allow-block-list/email-security",
    };
  },
  validateMspEmailSecurityAllowBlockList: () => {
    return {
      url: "/msp/settings/allow-block-list/email-security/validate",
      method: "POST",
    };
  },
  mspEmailSecurityExistingTlds: () => {
    return {
      url: "/msp/settings/allow-block-list/email-security/tlds/existing",
    };
  },
  mspEmailSecurityRestrictedTlds: () => {
    return {
      url: "/msp/settings/allow-block-list/email-security/tlds/restricted",
    };
  },

  mspImportEndpointSecurityAllowBlockList: () => ({
    url: "/msp/settings/allow-block-list/endpoint-security/csv",
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }),
  mspEndpointSecurityAllowBlockListExampleCSV: () => {
    return {
      url: "/msp/settings/allow-block-list/endpoint-security/csv/example",
    };
  },
  mspEndpointSecurityAllowBlockList: () => {
    return {
      url: "/msp/settings/allow-block-list/endpoint-security",
    };
  },
  mspEndpointSecurityAllowBlockListItem: (id: string) => {
    return {
      url: `/msp/settings/allow-block-list/endpoint-security/${id}`,
    };
  },
  cloudSecurityWidget: () => ({
    url: `/dashboard/widget/cloud-security`,
    method: "GET",
  }),
  emailSecurityWidget: () => ({
    url: `/dashboard/widget/email-security`,
    method: "GET",
  }),
  endpointSecurityWidget: () => ({
    url: `/dashboard/widget/endpoint-security`,
    method: "GET",
  }),
  userDataGovernanceWidget: () => ({
    url: `/dashboard/widget/user-data-governance`,
    method: "GET",
  }),
  endpointDataGovernanceWidget: () => ({
    url: `/dashboard/widget/endpoint-data-governance`,
    method: "GET",
  }),
  edrWidget: () => ({
    url: `/dashboard/widget/edr`,
    method: "GET",
  }),
  securityAwarenessWidget: () => ({
    url: `/dashboard/widget/securityAwareness`,
    method: "GET",
  }),
  // Overview Endpoints
  openTicketsOverview: () => ({
    url: `/dashboard/overview/tickets/open`,
    method: "GET",
  }),
  autoResolvedTicketsOverview: (fromTime: number) => ({
    url: `/dashboard/overview/tickets/auto-resolved`,
    method: "GET",
    params: {
      fromTime,
    },
  }),
  protectedUsersOverview: () => ({
    url: `/dashboard/overview/users`,
    method: "GET",
  }),
  protectedDevicesOverview: () => ({
    url: `/dashboard/overview/devices`,
    method: "GET",
  }),
  openTicketsSummaryWidget: () => ({
    url: `/dashboard/widget/tickets/open-summary`,
    method: "GET",
  }),
  healthScoreWidget: () => ({
    url: `/dashboard/widget/workspaces/health-score`,
    method: "GET",
  }),
  securityGapsWidget: () => ({
    url: `/dashboard/widget/security-gaps`,
    method: "GET",
  }),
  mspHierarchyTree: () => ({
    url: "/msp/hierarchy/tree",
    method: "GET",
  }),
  mspHierarchyTreeDetails: (workspaceId: string) => ({
    url: `/msp/hierarchy/tree/${workspaceId}/details`,
    method: "GET",
  }),
  mspHierarchyTreeBundleStats: (workspaceId: string) => ({
    url: `/msp/hierarchy/tree/${workspaceId}/details/bundle-stats`,
    method: "GET",
  }),
  mspBillableUsage: (workspaceId: string) => ({
    url: `/msp/hierarchy/tree/${workspaceId}/billable-usage`,
    method: "GET",
  }),
  mspExportBillingUsage: () => ({
    url: "/msp/exports/billing-usage",
    method: "POST",
  }),
  mspHierarchyTreeChildren: (workspaceId: string) => ({
    url: `/msp/hierarchy/tree/${workspaceId}/children-stats`,
    method: "GET",
  }),
  getOnboardingStatus: () => ({
    url: `/onboarding`,
    method: "GET",
  }),
  skipOnboardingStep: (step: SetupHubStep) => ({
    url: `/onboarding/steps/${step}/skip`,
    method: "POST",
  }),
  dismissOnboarding: () => ({
    url: `/onboarding/dismiss`,
    method: "POST",
  }),
  restoreOnboarding: () => ({
    url: `/onboarding/restore`,
    method: "POST",
  }),
  mcpOAuthAuthorize: () => ({
    url: "/mcp/oauth/authorize",
    method: "POST",
  }),
  mcpOAuthClientInfo: (clientId: string) => ({
    url: `/mcp/oauth/clients/${clientId}`,
    method: "GET",
  }),
} as const satisfies Record<
  string,
  AxiosRequestConfig | ((...args: never[]) => AxiosRequestConfig)
>;
